import React from "react";

import { Artefact } from "@/types/artefacts";
import {
  getCodeDisplayConfig,
  getCodeContainerClasses,
  getLanguageLabelClasses,
  getCodeBlockClasses,
  buildCodeMetadata,
} from "@/utils/codeUtils";
import { SyntaxHighlighter } from "@/components/Chat/ArtefactComponents/SyntaxHighlighter";
import { useUIPreferences } from "@/stores/UIPreferencesStore";

interface CodeArtefactViewerProps {
  artefact: Artefact;
}

/**
 * CodeArtefactViewer Component
 *
 * Displays code artefacts with enhanced syntax highlighting.
 * Supports line numbers toggle with localStorage persistence.
 * Falls back to styled code block for unsupported languages.
 * Delegates all logic to utility functions for better separation of concerns.
 */
export const CodeArtefactViewer: React.FC<CodeArtefactViewerProps> = ({ artefact }) => {
  const { showLineNumbers } = useUIPreferences();

  // Get display configuration from utilities
  const config = getCodeDisplayConfig(artefact);
  const containerClasses = getCodeContainerClasses(config);
  const labelClasses = getLanguageLabelClasses(config);
  const fallbackClasses = getCodeBlockClasses(config);
  const metadata = buildCodeMetadata(config);

  return (
    <div className={`${containerClasses} p-2`} data-artefact-id={artefact.id}>
      <div className={labelClasses}>{metadata}</div>
      <SyntaxHighlighter
        code={artefact.content}
        fallbackClassName={fallbackClasses}
        language={artefact.language}
        showLineNumbers={showLineNumbers}
      />
    </div>
  );
};
